import { useCallback } from 'react';
import {
  getUserData as getUserDataFromStorage,
  updateUserData as updateUserDataToStorage,
  saveJobSkillWithVersion as saveJobSkillWithVersionToStorage,
  saveJobSkills as saveJobSkillsToStorage,
  saveCompetenciesWithVersion as saveCompetenciesWithVersionToStorage,
  saveCompetencies as saveCompetenciesToStorage
} from '../utils/storage';
import { logger } from '../utils/logger';

/**
 * Custom hook for data persistence operations
 * @param {string} type - The analyzer type (cv, job, company)
 * @returns {object} Data persistence functions
 */
export const useDataPersistence = (type) => {
  /**
   * Save user data with proper type mapping
   */
  const saveUserData = useCallback(async (userId, data) => {
    if (!userId || typeof userId !== 'string') {
      throw new Error('saveUserData requires a valid userId string');
    }
    const userIdStr = userId;

    // Map the type to the correct key if needed
    const mappedType = type === 'cv' ? 'cv' : type;
    logger.info('[useDataPersistence]', `Saving ${type} data for user ${userIdStr}`);

    // Ensure data is valid
    if (!data) {
      logger.error('[useDataPersistence]', 'Attempted to save null or undefined data');
      throw new Error(`Cannot save empty ${type} data for user ${userIdStr}`);
    }

    // Use storage.js updateUserData for consistent caching and logging
    const result = await updateUserDataToStorage(mappedType, userIdStr, data);
    if (!result) {
      throw new Error(`Failed to persist ${type} data for user ${userIdStr}`);
    }
    logger.info('[useDataPersistence]', `Save result for ${type}:`, result);
    return result;
  }, [type]);

  /**
   * Get user data with proper type mapping
   */
  const getUserData = useCallback(async (dataType, userId) => {
    const dataTypeToUse = dataType || type;
    logger.info('[useDataPersistence]', `Loading user data type=${dataTypeToUse}, userId=${userId}`);

    // Ensure userId is a string
    if (!userId || typeof userId !== 'string') {
      throw new Error('getUserData requires a valid userId string');
    }
    const userIdStr = userId;

    // Use storage.js getUserData for consistent caching and logging
    const data = await getUserDataFromStorage(dataTypeToUse, userIdStr);
    logger.info('[useDataPersistence]', `Data loaded for ${dataTypeToUse}:`, data);
    return data;
  }, [type]);

  /**
   * Save skills or competencies based on type with version control
   */
  const saveItemsWithVersion = useCallback(async (userId, items, saveVersion = false) => {
    logger.info('[useDataPersistence]', `Saving ${type} items with version=${saveVersion}`);

    if (type === 'job') {
      if (saveVersion) {
        return await saveJobSkillWithVersionToStorage(userId, items);
      }
      return await saveJobSkillsToStorage(userId, items);
    }
    if (type === 'cv') {
      if (saveVersion) {
        return await saveCompetenciesWithVersionToStorage(userId, items);
      }
      return await saveCompetenciesToStorage(userId, items);
    }

    logger.error('[useDataPersistence]', `Unknown type for saving items: ${type}`);
    throw new Error(`Unsupported persistence type: ${type}`);
  }, [type]);

  /**
   * Save analysis result with proper structure
   */
  const saveAnalysisResult = useCallback(async (userId, result, metadata = {}) => {
    logger.info('[useDataPersistence]', `Saving ${type} analysis result`);
    if (!result || typeof result !== 'object') {
      throw new Error(`saveAnalysisResult requires a valid result object for type ${type}`);
    }

    // Persist the exact backend payload and append metadata only.
    const dataToSave = {
      ...result,
      ...metadata
    };

    return await saveUserData(userId, dataToSave);
  }, [type, saveUserData]);

  return {
    saveUserData,
    getUserData,
    saveItemsWithVersion,
    saveAnalysisResult
  };
};
